import { CaretLeftIcon, DownloadSimpleIcon, EnvelopeIcon, PhoneIcon } from '@phosphor-icons/react';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { CopyToClipboard } from '@/components/common/copy-to-clipboard';
import { OverflowTooltip } from '@/components/common/overflow-tooltip';
import type { OrderDetail } from '@/features/orders';
import { ORDER_STATUS_CHIP, OrderStatusControl, isPrePlacementStatus } from '@/features/orders';
import { formatPhoneUS } from '@/lib/utils/format';

import { OrderActionsMenu } from './order-actions-menu';
import styles from './order-detail-header.module.scss';

type Props = {
  order: OrderDetail;
  onBack: () => void;
  onInvoice: () => void;
  invoicing: boolean;
};

export function OrderDetailHeader({ order, onBack, onInvoice, invoicing }: Props) {
  const { customer, orderNumber } = order;
  // 122372 §3 — before payment the page is view-only: a static chip, nothing to act on
  // (ADR-0015 Amendment 1).
  const viewOnly = isPrePlacementStatus(order.status);
  const chip = ORDER_STATUS_CHIP[order.status];

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <button type="button" aria-label="Back to Orders" className={styles.back} onClick={onBack}>
          <CaretLeftIcon size={24} />
        </button>
        <div className={styles.identity}>
          <div className={styles.nameRow}>
            {/* One line with an ellipsis, so a long name can never squeeze the order number into
                wrapping ("#ORD-⏎65"); the full name is one hover away (ADO 121002). */}
            <OverflowTooltip title={customer.name}>
              <h1 className={styles.name}>{customer.name}</h1>
            </OverflowTooltip>
            <span className={styles.orderNo}>
              #{orderNumber} <CopyToClipboard value={orderNumber} />
            </span>
          </div>
          <div className={styles.contacts}>
            <span className={styles.contact}>
              <PhoneIcon size={16} aria-hidden />
              {formatPhoneUS(customer.phone)}
            </span>
            {customer.email ? (
              <span className={styles.contact}>
                <EnvelopeIcon size={16} aria-hidden />
                {customer.email}
              </span>
            ) : null}
            {viewOnly ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
          </div>
        </div>
      </div>
      {viewOnly ? null : (
        <div className={styles.right}>
          <OrderStatusControl order={order} />
          <Button
            variant="outline"
            startIcon={<DownloadSimpleIcon />}
            // `invoiceId`, never `paymentComplete` or `status` (ADO 117767 rule 8): an order
            // cancelled after a successful payment keeps its invoice, and one placed before
            // invoicing shipped has none however complete its payment was.
            disabled={!order.invoiceId}
            loading={invoicing}
            onClick={onInvoice}
            className={styles.invoiceButton}
          >
            Invoice
          </Button>
          <OrderActionsMenu order={order} />
        </div>
      )}
    </header>
  );
}
